import { useState, useEffect } from "react";
import { Mail, X, AlertCircle, RefreshCw, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFirebaseAuth } from "@/hooks/use-firebase-auth";
import { sendVerificationEmail, getCurrentUser } from "@/lib/firebase";
import { useToast } from "@/hooks/use-toast";

export function EmailVerificationBanner() {
  const { user } = useFirebaseAuth();
  const { toast } = useToast();
  const [isSending, setIsSending] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [emailVerified, setEmailVerified] = useState<boolean>(true);
  const [resendTimer, setResendTimer] = useState(0);

  // Refresh emailVerified state when user changes
  useEffect(() => {
    if (user) {
      const currentUser = getCurrentUser();
      setEmailVerified(currentUser?.emailVerified || false);
    }
  }, [user]);

  // Timer countdown effect
  useEffect(() => {
    if (resendTimer > 0) {
      const interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [resendTimer]);

  // Don't show if no user, user is verified, or banner was dismissed
  if (!user || isDismissed || emailVerified === true) return null;

  const handleResendVerification = async () => {
    setIsSending(true);
    try {
      const currentUser = getCurrentUser();
      if (!currentUser) {
        throw new Error('No user logged in');
      }

      // Use Firebase's built-in email verification
      await sendVerificationEmail(currentUser);

      toast({
        title: "Verification Email Sent",
        description: "Please check your inbox and spam folder for the verification email.",
      });
      // Start 60-second countdown
      setResendTimer(60);
    } catch (error: any) {
      // Handle specific Firebase auth errors
      let errorMessage = "Failed to send verification email. Please try again later.";
      if (error.message?.includes("already verified") || error.code === 'auth/email-already-verified') {
        toast({
          title: "Already Verified",
          description: "Your email is already verified. Please refresh the page.",
        });
        // Auto refresh after showing success
        setTimeout(() => window.location.reload(), 2000);
        return;
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = "Too many verification emails sent. Please wait before trying again.";
      } else if (error.code === 'auth/user-token-expired') {
        errorMessage = "Session expired. Please log in again.";
      }
      
      toast({
        title: "Error Sending Email",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleCheckVerification = async () => {
    try {
      const currentUser = getCurrentUser();
      await currentUser?.reload(); // Refresh user state
      const updatedUser = getCurrentUser();
      
      if (updatedUser?.emailVerified) {
        setEmailVerified(true);
        toast({
          title: "Email Verified!",
          description: "Your email has been successfully verified.",
        });
        // Trigger auth refresh
        document.dispatchEvent(new CustomEvent('auth-refresh-required'));
      } else {
        toast({
          title: "Not Verified Yet",
          description: "Please check your email and click the verification link, then try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to check verification status. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    // Store dismissal with timestamp - show again after 24 hours
    const dismissalData = {
      timestamp: Date.now(),
      userId: user.id
    };
    localStorage.setItem("email-verification-banner-dismissed", JSON.stringify(dismissalData));
  };

  // Check if previously dismissed (but allow after 24 hours)
  const checkDismissal = () => {
    const dismissalData = localStorage.getItem("email-verification-banner-dismissed");
    if (dismissalData) {
      try {
        const { timestamp, userId } = JSON.parse(dismissalData);
        // Show again if it's been 24 hours OR different user
        const twentyFourHours = 24 * 60 * 60 * 1000;
        if (userId === user.id && Date.now() - timestamp < twentyFourHours) {
          return true; // Still dismissed
        }
      } catch (e) {
        // Invalid data, clear it
        localStorage.removeItem("email-verification-banner-dismissed");
      }
    }
    return false; // Not dismissed or expired
  };

  if (checkDismissal()) {
    return null;
  }

  return (
    <div className="bg-black border-b border-gray-800">
      <div className="container mx-auto px-4 py-3">
        {/* Stack vertically on mobile so the email + buttons don't push the
            page wider than the viewport. Horizontal again ≥sm. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start sm:items-center space-x-3 flex-1 min-w-0">
            <div className="bg-gray-800 rounded-full p-2 flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-brand" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white">
                Verify your email address
              </p>
              <p className="text-xs text-gray-400 mt-0.5 break-words">
                Check your <span className="text-brand break-all">{user.email}</span> inbox and spam folder for the verification email.
              </p>
            </div>
          </div>
          {/* Action buttons — wrap on mobile, inline on desktop */}
          <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                onClick={handleCheckVerification}
                className="bg-brand text-black hover:bg-brand/90"
              >
                <Check className="h-3 w-3 mr-1.5" />
                I've Verified
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleResendVerification}
                disabled={isSending || resendTimer > 0}
                className="bg-transparent border-gray-600 text-gray-300 hover:bg-gray-800 disabled:opacity-50"
              >
                {isSending ? (
                  <>
                    <RefreshCw className="h-3 w-3 mr-1.5 animate-spin" />
                    Sending...
                  </>
                ) : resendTimer > 0 ? (
                  <>
                    <Mail className="h-3 w-3 mr-1.5" />
                    Resend in {resendTimer}s
                  </>
                ) : (
                  <>
                    <Mail className="h-3 w-3 mr-1.5" />
                    Resend Email
                  </>
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                className="text-gray-400 hover:text-gray-300 hover:bg-gray-800"
              >
                <X className="h-4 w-4" />
              </Button>
          </div>
        </div>
      </div>
    </div>
  );
}